import React from 'react';

import styled from '@tanium/react-emotion-9';
import { css, defineVariant } from '@tanium/style-system';

import { BodyFill, ContainerBorder, ModalShadow } from './themeDefinitions';

export type ModalSize = 'sm' | 'md' | 'lg';

export interface ModalContainerProps {
  /**
   * The contents to display inside the modal container.
   */
  children?: React.ReactNode;
  /**
   * The size of the modal container. May be `sm`, `md`, or `lg`; defaults to `md`.
   */
  size?: ModalSize;
}

const sizeVariant = defineVariant<ModalSize>(
  {
    sm: {
      ['@media (min-width: 768px)' as string]: {
        width: '320px',
      },
    },
    md: {
      ['@media (min-width: 768px)' as string]: {
        width: '640px',
      },
    },
    lg: {
      ['@media (min-width: 992px)' as string]: {
        width: '960px',
      },
    },
  },
  { defaultVariant: 'md' },
);

const ModalContainer = styled('div', {
  shouldForwardProp: (p) => p !== 'size' && p !== 'innerRef',
})<ModalContainerProps>(
  css({
    width: 'auto',
    margin: 1,
    position: 'relative',
    boxShadow: ModalShadow,
    backgroundColor: BodyFill,
    backgroundClip: 'padding-box',
    border: 1,
    borderColor: ContainerBorder,
    borderRadius: 4,
    boxSizing: 'border-box',
    outline: 'none',
    ['@media (min-width: 768px)' as string]: {
      my: 4,
      mx: 'auto',
    },
  }),
  ({ size }) => sizeVariant(size),
);

export default React.forwardRef<HTMLDivElement, ModalContainerProps>((props, ref) => (
  <ModalContainer innerRef={ref} {...props} />
));
